import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EntityPicker, customerRender } from "@/components/app/EntityPicker";
import { useEffect, useMemo } from "react";
import { useFetch } from "@/hooks/useFetch";
import { useMe } from "@/components/app/MeContext";

export const prospectRender = (p) => ({ title: p.company_name, subtitle: `${p.code}${p.city ? ` · ${p.city}` : ""}` });
const TEXT = [["contact_name", "Contact name"], ["contact_email", "Contact email"], ["contact_phone", "Contact phone"], ["site_name", "Site name"],
  ["project_reference", "Project reference"]];

export const quoteToForm = (q) => ({
  ...q, customer: q.customer_id ? { id: q.customer_id, name: q.company_name_snapshot, code: q.party_snapshot?.code, gstins: [] } : null,
  prospect: q.prospect_id ? { id: q.prospect_id, company_name: q.company_name_snapshot, code: q.party_snapshot?.code } : null,
});
export const formToBody = (f) => ({
  party_type: f.party_type, customer_id: f.customer?.id || null, prospect_id: f.prospect?.id || null, registration_id: f.registration_id || null,
  ...Object.fromEntries([...TEXT.map((t) => t[0]), "quote_date", "valid_until", "remarks", "currency"].map((k) => [k, f[k] || ""])),
  ...Object.fromEntries(["sales_owner_id", "payment_term_id", "delivery_term_id", "warranty_term_id", "terms_template_id"].map((k) => [k, f[k] || null])), terms_text: f.terms_text ?? null,
});

function TermSelect({ kind, label, value, onChange }) {
  const { data } = useFetch(`/sales-terms?kind=${kind}`);
  const rows = (data || []).filter((t) => t.active || t.id === value);
  return (
    <div><Label>{label}</Label>
      <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? null : v, rows.find((t) => t.id === v))}>
        <SelectTrigger className="bg-white" data-testid={`quote-${kind}-term`}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="none">{rows.length ? "Not set" : "None in Terms masters yet"}</SelectItem>
          {rows.map((t) => <SelectItem key={t.id} value={t.id}>{t.code} — {t.name}</SelectItem>)}</SelectContent>
      </Select></div>
  );
}

function SalesOwnerSelect({ value, onChange }) {
  const { me } = useMe();
  const { data } = useFetch("/sales-people");
  const rows = useMemo(() => (data || []).filter((p) => p.active || p.id === value), [data, value]);
  useEffect(() => {
    const mine = !value && rows.find((p) => p.active && p.email === me.email?.toLowerCase());
    if (mine) onChange(mine.id);
  }, [value, rows, me.email, onChange]);
  return (
    <div className="col-span-2"><Label>Sales owner (Sales Team master) *</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="bg-white" data-testid="quote-sales-owner"><SelectValue placeholder={rows.length ? "Pick the sales person" : "No one in the Sales Team yet — ask an admin"} /></SelectTrigger>
        <SelectContent>{rows.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.designation ? ` · ${p.designation}` : ""}</SelectItem>)}</SelectContent>
      </Select></div>
  );
}

function Party({ f, set, lockParty }) {
  const { data: cust } = useFetch(f.party_type === "customer" && f.customer?.id ? `/customers/${f.customer.id}` : null);
  const regs = cust?.registrations || [];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div><Label>Quote for</Label>
        <div className="inline-flex rounded border border-slate-200 bg-slate-50 p-0.5 w-full">
          {[["customer", "Customer"], ["prospect", "Prospect"]].map(([k, l]) => (
            <button key={k} type="button" disabled={lockParty} onClick={() => set({ party_type: k, customer: null, prospect: null, registration_id: null })} data-testid={`quote-party-${k}`}
              className={`flex-1 px-3 py-1.5 text-xs rounded transition-colors ${f.party_type === k ? "bg-white shadow-sm font-semibold" : "text-slate-500"} disabled:opacity-60`}>{l}</button>
          ))}
        </div></div>
      <div className="md:col-span-2"><Label>{f.party_type === "customer" ? "Customer (Customers Master) *" : "Prospect (Prospect Master) *"}</Label>
        {lockParty ? <div className="h-9 flex items-center px-3 border rounded bg-slate-50 text-sm" data-testid="quote-party-locked">{f.company_name_snapshot}</div>
          : f.party_type === "customer"
            ? <EntityPicker url="/customers?active=true" value={f.customer} onChange={(c) => set({ customer: c, registration_id: null, contact_name: c?.contact_name || "", contact_phone: c?.phone || "", contact_email: c?.email || "" })} render={customerRender} placeholder="Search name, code or GSTIN" testid="quote-customer-picker" />
            : <EntityPicker url="/prospects?active=true" value={f.prospect} onChange={(p) => set({ prospect: p, contact_name: p?.contact_name || "", contact_phone: p?.contact_phone || "", contact_email: p?.contact_email || "" })} render={prospectRender} placeholder="Search prospects (add new ones under Prospects)" testid="quote-prospect-picker" />}
      </div>
      {regs.length > 1 && <div className="md:col-span-3"><Label>Billing GSTIN</Label>
        <Select value={f.registration_id || ""} onValueChange={(v) => set({ registration_id: v })}>
          <SelectTrigger className="bg-white" data-testid="quote-registration"><SelectValue placeholder="Pick GSTIN (optional on a quote)" /></SelectTrigger>
          <SelectContent>{regs.map((r) => <SelectItem key={r.id} value={r.id}>{r.gstin || "No GSTIN"}{r.state ? ` · ${r.state}` : ""}{r.label ? ` · ${r.label}` : ""}</SelectItem>)}</SelectContent>
        </Select></div>}
    </div>
  );
}

export function QuoteHeaderForm({ f, setF, lockParty }) {
  const set = (patch) => setF({ ...f, ...patch });
  return (
    <div className="space-y-4" data-testid="quote-header-form">
      <Party f={f} set={set} lockParty={lockParty} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><Label>Quotation date</Label><Input type="date" value={f.quote_date || ""} onChange={(e) => set({ quote_date: e.target.value })} data-testid="quote-date" /></div>
        <div><Label>Valid until</Label><Input type="date" value={f.valid_until || ""} onChange={(e) => set({ valid_until: e.target.value })} data-testid="quote-valid-until" /></div>
        {TEXT.map(([k, l]) => <div key={k}><Label>{l}</Label><Input value={f[k] || ""} onChange={(e) => set({ [k]: e.target.value })} data-testid={`quote-${k.replace(/_/g, "-")}`} /></div>)}
        <SalesOwnerSelect value={f.sales_owner_id} onChange={(v) => setF((x) => ({ ...x, sales_owner_id: v }))} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <TermSelect kind="payment" label="Payment terms" value={f.payment_term_id} onChange={(v) => set({ payment_term_id: v })} />
        <TermSelect kind="delivery" label="Delivery terms / lead time" value={f.delivery_term_id} onChange={(v) => set({ delivery_term_id: v })} />
        <TermSelect kind="warranty" label="Warranty terms" value={f.warranty_term_id} onChange={(v) => set({ warranty_term_id: v })} />
        <TermSelect kind="template" label="T&C template" value={f.terms_template_id} onChange={(v, t) => set({ terms_template_id: v, terms_text: t ? t.description : f.terms_text })} />
      </div>
      <div><Label>Terms & conditions text (from the template; can be adjusted for this quote)</Label>
        <Textarea rows={4} value={f.terms_text || ""} onChange={(e) => set({ terms_text: e.target.value })} data-testid="quote-terms-text" /></div>
      <div><Label>Remarks</Label><Textarea rows={2} value={f.remarks || ""} onChange={(e) => set({ remarks: e.target.value })} data-testid="quote-remarks" /></div>
    </div>
  );
}
